// NOTE: Metadata implicitly added to every Statsig event from app/main.

import type { SemboBenefitsType } from '@apps/main/new-desktop/modules/semboBenefits';

// NOTE: Statsig events accept strings, hence primitive values are transformed to string representation.
type BooleanString = 'true' | 'false';

export interface BaseMetadata {
  isDesktop: BooleanString;
  language: string;
  domain: string;
  dateMode: 'flexible' | 'selectedDate';
}

// NOTE: Core

export const enum CoreEventSource {
  ActivitySelection = 'activitySelection',
  HotelSelection = 'hotelSelection',
  TripOverview = 'tripOverview',
  FlightList = 'flightList',
  FerryList = 'ferryList',
  MonthsSelector = 'monthsSelector',
  TripSummary = 'tripSummary',
  HotelDetails = 'hotelDetails',
  CarTrip = 'carTrip',
}

export interface CoreEventMetadata {
  source: CoreEventSource;
}

// NOTE: Trip overview

export interface TripOverviewBookClickedMetadata {
  totalIngredients: number;
  totalAccomodation: number;
  totalOwnStay: number;
  totalFlight: number;
  totalFerry: number;
  totalActivity: number;
  totalAmount: number;
  market: string;
  priceType: 'totalPrice' | 'grossDiscount';
}

export const enum EditTripModalOpenedLocationType {
  TripOverviewAddDestinationButton = 'TripOverviewAddDestinationButton',
  TripOverviewEditTripButton = 'TripOverviewEditTripButton',
  TripOverviewDestinationSegment = 'TripOverviewDestinationSegment',
  TripOverviewAddDestinationToOwnStayButton = 'TripOverviewAddDestinationToOwnStayButton',
}

export interface TripOverviewUnavailableProductActionClickedMetadata
  extends Record<string, string> {
  actionType: 'changeFilters' | 'changeProduct' | 'changeDate';
}

export interface TripOverviewUnavailableProductRenderedMetadata
  extends Record<string, string> {
  productType: 'accommodation' | 'activity' | 'flight' | 'ferry';
  isSelectedProduct: BooleanString;
  hasSelectedFilters: BooleanString;
}

export interface TripOverviewHeaderActionsMetadata
  extends Record<string, string> {
  actionType:
    | 'editTrip'
    | 'paxSelector'
    | 'monthsSelector'
    | 'homeAirport'
    | 'editStay';
}

export interface TripOverviewProductTourStepMetadata
  extends Record<string, string> {
  detail: 'change_hotel' | 'nights' | 'pax' | 'calendar' | 'transport';
  step: '1' | '2' | '3' | '4' | '5';
}

export interface TripOverviewPreferencesModalMetadata
  extends Record<string, string> {
  source: 'from-plus' | 'from-more';
}

// NOTE: Hotel details

export const enum HotelDetailsEventSource {
  HotelSelection = 'hotelSelection',
  HotelDetailsSelectionBox = 'hotelDetailsSelectionBox',
  TripOverview = 'tripOverview',
  TripOverviewAlternativeHotels = 'tripOverviewAlternativeHotels',
  StandaloneView = 'standaloneView',
}

export interface HotelDetailsEventMetadata {
  source: HotelDetailsEventSource;
}

// NOTE: Change hotel
export const enum ChangeHotelEventSource {
  AvailableAccommodation = 'availableAccommodation',
  AlternativeAccommodation = 'alternativeAccommodation',
  UnavailableAccommodation = 'unavailableAccommodation',
}

export interface ChangeHotelEventMetadata {
  source: ChangeHotelEventSource;
}

// NOTE: Hotel selection
export const enum CloseHotelSelectionFiltersOrigin {
  ShowResultsButton = 'show results button',
  CloseButton = 'close button',
}

export const enum RemoveHotelSelectionFiltersOrigin {
  FilterList = 'filter list',
  HotelList = 'hotel list',
  TripOverview = 'trip overview',
  HotelDetails = 'hotel details',
}

export interface HotelSelectionInitiatedMetadata {
  placeId: number | undefined;
  placeName: string;
  hotelResults: number;
}

export interface HotelSelectionFilterOpenedMetadata {
  polygonId: number | undefined;
  label: string;
  hotelResults: number;
}

export interface HotelSelectionFilterClosedMetadata {
  polygonId: number | undefined;
  label: string;
  hotelResults: number;
  eventPlacement: CloseHotelSelectionFiltersOrigin;
}

export interface HotelSelectionFilterAppliedMetadata {
  filterName: string;
  filterGroup: string;
  filterDetail: string;
  polygonId: number | undefined;
  pathname: string;
  eventPlacement: RemoveHotelSelectionFiltersOrigin;
}

export interface HotelSelectionFilterRemovedMetadata {
  filterName: string;
  filterGroup: string;
  filterDetail: string;
  polygonId: number | undefined;
  pathname: string;
  eventPlacement: RemoveHotelSelectionFiltersOrigin;
}

export interface HotelSelectionFilterResetMetadata {
  polygonId: number | undefined;
  label: string;
}

export interface HotelSelectionListCardClickedMetaData {
  isPreviouslySeenHotel: string;
}
export interface HotelSelectionSemboBenefitsMetadata {
  benefit: SemboBenefitsType;
}

export interface ActivityDetailsTicketSelectedMetadata {
  productCode: string;
}

export interface TripOverviewPrintModeMetadata {
  source: 'button' | 'shortcut';
}

export interface MissingCarrierLogoMetadata {
  carrierCode: string;
}

export interface MissingDestinationInformationMetadata {
  polygonId: number;
  missingReason: 'MissingDescription' | 'MissingImage' | 'NotFound';
}
